import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import Slider from 'react-slick';
import './ResultsSection.css';

const Clock = () => (
    <div className="clock">
        <div className="hour-hand"></div>
        <div className="minute-hand"></div>
        <div className="clock-center"></div>
    </div>
);

const NumberTicker = () => {
    const [currentStatIndex, setCurrentStatIndex] = useState(0);
    const [count, setCount] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [direction, setDirection] = useState('next');
    const [isVisible, setIsVisible] = useState(false);
    const tickerRef = useRef(null);
    const countInterval = useRef(null);

    const statistics = useMemo(() => [
        {
            number: 10000,
            text: "Students have cleared written defence exams",
            suffix: "+",
            showTicker: true
        },
        {
            number: 650,
            text: "Officers commissioned in Indian Armed Forces",
            suffix: "+",
            showTicker: true
        },
        {
            number: 54,
            text: "Students cleared NDA written exam in 2024",
            suffix: "",
            showTicker: true
        },
        {
            number: 14,
            text: "SSB recommendations in 2024",
            suffix: "",
            showTicker: true
        },
        {
            number: 38,
            text: "Years of excellence in defense training",
            suffix: "+",
            showTicker: true
        },
        {
            text: "Classes available from 6 AM to 11 PM daily, regardless of holidays",
            showClock: true,
            showTicker: false
        }
    ], []);

    const startCounting = useCallback((targetNumber) => {
        if (countInterval.current) {
            clearInterval(countInterval.current);
        }
        
        setCount(0);
        const duration = 2000; // 2 seconds
        const steps = 50;
        const increment = Math.ceil(targetNumber / steps);
        const stepTime = duration / steps;

        countInterval.current = setInterval(() => {
            setCount(prev => {
                const next = prev + increment;
                if (next >= targetNumber) {
                    clearInterval(countInterval.current);
                    return targetNumber;
                }
                return next;
            });
        }, stepTime);
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        const currentTickerRef = tickerRef.current;
        if (currentTickerRef) {
            observer.observe(currentTickerRef);
        }

        return () => {
            if (currentTickerRef) {
                observer.unobserve(currentTickerRef);
            }
            if (countInterval.current) {
                clearInterval(countInterval.current);
            }
        };
    }, []);

    useEffect(() => {
        if (isVisible) {
            const currentStat = statistics[currentStatIndex];
            if (currentStat.showTicker) {
                startCounting(currentStat.number);
            }
        }
    }, [currentStatIndex, isVisible, statistics, startCounting]);

    useEffect(() => {
        if (isVisible) {
            const timer = setInterval(() => {
                setDirection('next');
                setIsTransitioning(true);
                
                setTimeout(() => {
                    setCurrentStatIndex((prev) => (prev + 1) % statistics.length);
                    setTimeout(() => {
                        setIsTransitioning(false);
                    }, 50);
                }, 500);
            }, 4000);

            return () => clearInterval(timer);
        }
    }, [isVisible, statistics.length]);

    const getContentClassName = () => {
        if (!isTransitioning) return 'ticker-content';
        return `ticker-content ${direction === 'next' ? 'next-enter' : 'prev-exit'} ${
            isTransitioning ? 'animating' : ''
        }`;
    };

    const currentStat = statistics[currentStatIndex];

    return (
        <div className="ticker-container" ref={tickerRef}>
            <div className={getContentClassName()}>
                {currentStat.showClock ? (
                    <div className="ticker-icon">
                        <Clock />
                    </div>
                ) : (
                    <div className="ticker-number">
                        {currentStat.showTicker ? count : currentStat.number}
                        {currentStat.suffix}
                    </div>
                )}
                <div className="ticker-text">{currentStat.text}</div>
            </div>
        </div>
    );
};

const ResultsSection = () => {
    const [playingStates, setPlayingStates] = useState({});
    const [currentSlide, setCurrentSlide] = useState(0);
    const videoRefs = useRef({});
    const sliderRef = useRef(null);
    const isTransitioning = useRef(false);
    const observerRef = useRef(null);

    useEffect(() => {
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    const videoIndex = entry.target.dataset.index;
                    if (!entry.isIntersecting && videoRefs.current[videoIndex]) {
                        const video = videoRefs.current[videoIndex];
                        if (!video.paused) {
                            video.pause();
                            handleVideoPause(parseInt(videoIndex));
                        }
                    }
                });
            },
            {
                threshold: 0.3 // Trigger when 30% of the video is out of view
            }
        );

        // Cleanup observer
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    // Update observer whenever video refs change
    useEffect(() => {
        Object.keys(videoRefs.current).forEach(index => {
            const video = videoRefs.current[index];
            if (video && observerRef.current) {
                observerRef.current.observe(video);
            }
        });

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [currentSlide]);

    const studentAchievements = [
        {
            name: "Vaishnavi Kudale",
            achievement: "Recommended for Indian Army | 24 SSB Bangalore",
            video: "https://videos.adcionline.com/student1.mp4",
            badge: "Selected"
        },
        {
            name: "Druva Phalke",
            achievement: "NDA 154 Course Qualified | Bhartiya Vidyapeeth CS",
            video: "https://videos.adcionline.com/student2.mp4",
            badge: "NDA"
        },
        {
            name: "Shravani Kadgi",
            achievement: "NDA 2 2024 Written | SSB Qualified",
            video: "https://videos.adcionline.com/student3.mp4",
            badge: "SSB"
        },
        {
            name: "Vitthal Keshav Shinde",
            achievement: "NDA 2 2024 Written (3rd Attempt) | Vaijapur",
            video: "https://videos.adcionline.com/student4.mp4",
            badge: "NDA"
        },
        {
            name: "Priyanshu Khandekar",
            achievement: "NDA 2024 | JEE Mains | MHT CET: VIT Pune | IMU CET",
            video: "https://videos.adcionline.com/student5.mp4",
            badge: "Multi-Qualified"
        }
    ];

    const handleVideoPlay = (index) => {
        setPlayingStates(prev => ({ ...prev, [index]: true }));
    };

    const handleVideoPause = (index) => {
        setPlayingStates(prev => ({ ...prev, [index]: false }));
    };

    const handleVideoEnded = (index) => {
        setPlayingStates(prev => ({ ...prev, [index]: false }));
    };

    const togglePlayPause = (index) => {
        const video = videoRefs.current[index];
        if (video) {
            if (playingStates[index]) {
                video.pause();
                handleVideoPause(index);
            } else {
                video.play();
                handleVideoPlay(index);
            }
        }
    };

    const handleDotClick = (index) => {
        if (sliderRef.current) {
            sliderRef.current.slickGoTo(index);
        }
    };

    const handleNextSlide = () => {
        if (sliderRef.current) {
            sliderRef.current.slickNext();
        }
    };

    const settings = {
        dots: false,
        infinite: true,
        speed: 800,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: false,
        arrows: false,
        fade: true,
        cssEase: 'cubic-bezier(0.4, 0, 0.2, 1)',
        pauseOnHover: true,
        beforeChange: (oldIndex, newIndex) => {
            isTransitioning.current = true;
            const oldVideo = videoRefs.current[oldIndex];
            if (oldVideo) {
                oldVideo.pause();
                handleVideoPause(oldIndex);
            }
            // Add a small delay before transitioning to the next slide
            setTimeout(() => {
                setCurrentSlide(newIndex);
            }, 50);
        },
        afterChange: (index) => {
            isTransitioning.current = false;
            setCurrentSlide(index);
        }
    };

    const renderVideo = (video, index) => (
        <video
            ref={el => videoRefs.current[index] = el}
            data-index={index}
            className="results-video"
            src={video}
            preload="auto" // added to preload video beforehand
            playsInline
            onPlay={() => handleVideoPlay(index)}
            onPause={() => handleVideoPause(index)}
            onEnded={() => handleVideoEnded(index)}
        />
    );

    const NavigationDots = () => (
        <div className="dots-container">
            {studentAchievements.map((_, index) => (
                <div
                    key={index}
                    className={`nav-dot ${currentSlide === index ? 'active' : ''}`}
                    onClick={() => handleDotClick(index)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Go to slide ${index + 1}`}
                />
            ))}
        </div>
    );

    return (
        <section className="results-section">
            <div className="student-testimonial-text">
                "OUR RESULTS SPEAKS LOUDER THAN OUR WORDS"
            </div>
            <NumberTicker />
            <div className="testimonial-section-wrapper">
                <div className="testimonial-section">
                    <div className="results-title-wrapper">
                        <h2 className="results-title">Students with their experiences.</h2>
                    </div>
                    <div className="results-slider">
                        <Slider ref={sliderRef} {...settings}>
                            {studentAchievements.map((student, index) => (
                                <div key={index} className="result-slide">
                                    <div className="result-content">
                                        <div className="video-container">
                                            {renderVideo(student.video, index)}
                                            <div 
                                                className={`play-button-overlay ${playingStates[index] ? 'playing' : ''}`}
                                                onClick={() => togglePlayPause(index)}
                                            >
                                                <div className="play-icon">
                                                    {playingStates[index] ? (
                                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <rect x="6" y="4" width="4" height="16" rx="1" fill="white"/>
                                                            <rect x="14" y="4" width="4" height="16" rx="1" fill="white"/>
                                                        </svg>
                                                    ) : (
                                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M8 5V19L19 12L8 5Z" fill="white"/>
                                                        </svg>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="achievement-info">
                                            <span className="achievement-badge" data-badge={student.badge}>{student.badge}</span>
                                            <h3 className="student-name">{student.name}</h3>
                                            <p className="student-achievement">{student.achievement}</p>
                                        </div>
                                        <div className="see-more-button" onClick={handleNextSlide}>
                                            <span>See More</span>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </Slider>
                        <NavigationDots />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ResultsSection;
